import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faEye,
  faUserPlus,
  faFilter,
  faRotate,
  faTrash,
  faPen
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../../components/ConfirmModal';
import Table from '../../../components/Table';
import Pagination from '../../../components/Pagination';
import ColumnVisibilityModal from '../../../components/ColumnVisibilityModal';
import { usuarioService } from '../../../services/usuarioService';
import { extensaoService } from '../../../services/extensaoService';
import { usuarioExtensaoService } from '../../../services/usuarioExtensaoService';

const pageSizes = [5, 10, 20, 50];

const UsuarioExtensaoAccess = () => {
  const navigate = useNavigate();

  // Estados para filtros
  const [filters, setFilters] = useState({
    search: '',
    usuario_id: '',
    extensao_id: '',
    status: ''
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [acessos, setAcessos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [extensoes, setExtensoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1
  });

  // Estados de modais
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAccess, setSelectedAccess] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(['id', 'usuario_nome', 'extensao_nome', 'ativo', 'criado_em']);

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  // Configuração das colunas
  const columns = [
    { Header: 'ID', accessor: 'id' },
    { 
      Header: 'Usuário', 
      accessor: 'usuario_nome',
      Cell: (value, row) => (
        <div className="flex items-center gap-2">
          <span>{row.usuario?.nome || 'N/A'}</span>
          {row.usuario?.id && (
            <div className="flex gap-1">
              <button
                onClick={() => navigate(`/seguranca/usuarios/extensoes/${row.id}`)}
                className="text-blue-600 hover:text-blue-800 p-1"
                title="Visualizar Usuário"
              >
                <FontAwesomeIcon icon={faEye}  />
              </button>
              <button
                onClick={() => navigate(`/seguranca/usuarios/extensoes/${row.id}/edit`)}
                className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                title="Editar Usuário"
              >
                <FontAwesomeIcon icon={faPen}  />
              </button>
            </div>
          )}
        </div>
      )
    },
    { 
      Header: 'Extensão', 
      accessor: 'extensao_nome',
      Cell: (value, row) => row.extensao?.nome || 'N/A'
    },
    { 
      Header: 'Ativo', 
      accessor: 'ativo',
      Cell: (value) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Ativo' : 'Inativo'}
        </span>
      )
    },
    { 
      Header: 'Data Criação', 
      accessor: 'criado_em',
      Cell: (value) => {
        if (!value) return 'N/A';
        const date = new Date(value);
        const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
        return adjustedDate.toLocaleDateString('pt-BR');
      }
    },

  ];

  // Carregar acessos
  const loadAcessos = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pageSize,
        ...filters
      };
      const response = await usuarioExtensaoService.list(params);
      
      setAcessos(response.data || []);
      setPagination({
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 0,
        currentPage: response.pagination?.page || 1
      });
    } catch (error) {
      console.error('Erro ao carregar acessos:', error);
      setAcessos([]);
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados auxiliares
  const loadUsuarios = async () => {
    try {
      const response = await usuarioService.list({ limit: 1000 });
      setUsuarios(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const loadExtensoes = async () => {
    try {
      const response = await extensaoService.list({ limit: 1000 });
      console.log('Extensões carregadas:', response.data);
      setExtensoes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar extensões:', error);
    }
  };

  // Deletar acesso
  const handleDelete = async () => {
    try {
      await usuarioExtensaoService.delete(selectedAccess.id);
      alert('Acesso excluído com sucesso!');
      setShowDeleteModal(false);
      setSelectedAccess(null);
      loadAcessos();
    } catch (error) {
      alert('Erro ao excluir acesso.');
    }
  };

  // Filtros
  const clearFilters = () => {
    setFilters({
      search: '',
      usuario_id: '',
      extensao_id: '',
      status: ''
    });
    setPage(1);
  };

  // Effects
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadAcessos();
  }, [page, pageSize, filters]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    loadUsuarios();
    loadExtensoes();
  }, []);

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Gerenciar Acesso de Usuários a Extensões
        </h1>
        <p className="text-gray-600">
          Gerencie os acessos de usuários às extensões do sistema
        </p>
      </div>

      {/* Ações */}
      <div className="flex justify-end items-center mb-6">
        <div className="flex space-x-2">
          <button
            onClick={() => navigate('/seguranca/usuarios/extensoes/create')}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Novo Acesso
          </button>
          <button
            onClick={() => navigate('/seguranca/usuarios/extensoes/bulk-create')}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
            Criação em Massa
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            Filtros
          </h3>
          <button
            onClick={clearFilters}
            className="px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
          >
            <FontAwesomeIcon icon={faRotate} className="mr-1" />
            Limpar
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Usuário
            </label>
            <select
              value={filters.usuario_id}
              onChange={(e) => setFilters({...filters, usuario_id: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-green-500"
            >
              <option value="">Todos os usuários</option>
              {usuarios.map(usuario => (
                <option key={usuario.id} value={usuario.id}>
                  {usuario.nome}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Extensão
            </label>
            <select
              value={filters.extensao_id}
              onChange={(e) => {
                console.log('Filtro extensão selecionado:', e.target.value);
                setFilters({...filters, extensao_id: e.target.value});
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-green-500"
            >
              <option value="">Todas as extensões</option>
              {extensoes.map(extensao => (
                <option key={extensao.idExtensao} value={extensao.idExtensao}>
                  {extensao.nome}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-green-500"
            >
              <option value="">Todos</option>
              <option value="1">Ativo</option>
              <option value="0">Inativo</option>
            </select>
          </div>
        </div>
      </div>

      {/* Modal de Visibilidade de Colunas */}
      <ColumnVisibilityModal
        columns={columns}
        visibleColumns={visibleColumns}
        onChange={handleToggleColumn}
        onClose={handleCloseModal}
        isOpen={isModalOpen}
      />

      {/* Controles de Paginação e Colunas */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
        <div className="order-2 sm:order-1 w-full sm:w-auto">
          <Pagination 
            currentPage={pagination.currentPage} 
            totalPages={pagination.totalPages} 
            onPageChange={setPage} 
          />
        </div>
        <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
          <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            {pageSizes.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
          <button
            type="button"
            className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
            onClick={handleOpenModal}
            aria-label="Exibir/Ocultar colunas"
          >
            <FontAwesomeIcon icon={faEye} />
            <span>Colunas</span>
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white shadow-sm rounded-lg">
        <Table
          columns={columns.filter(col => visibleColumns.includes(col.accessor))}
          data={acessos}
          loading={loading}
          emptyMessage="Nenhum acesso encontrado."
        />
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir este acesso?`}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteModal(false)}
        confirmText="Excluir"
        cancelText="Cancelar"
      />
    </div>
  );
};

export default UsuarioExtensaoAccess;
